import React, { useState, useEffect, useRef } from 'react';
import { Search, Book, Pill, Star, Clock, Eye, Heart, ChevronRight, User, MessageCircle, HelpCircle } from 'lucide-react';
import { HealthArticle, Drug } from '../types';

interface DiscoverProps {
  articles: HealthArticle[];
  drugs: Drug[];
  onNavigate: (section: string, data?: any) => void;
  navigationData?: any;
  currentUser?: any | null;
  handleAuthRequired?: (message: string, mode?: 'signin' | 'signup') => void;
}

const Discover: React.FC<DiscoverProps> = ({ 
  articles, 
  drugs, 
  onNavigate,
  navigationData,
  currentUser,
  handleAuthRequired
}) => {
  const [activeTab, setActiveTab] = useState('articles');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<HealthArticle | null>(null);
  const [selectedDrug, setSelectedDrug] = useState<Drug | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showAskExpert, setShowAskExpert] = useState(false);
  
  // Use ref to maintain search input focus
  const searchInputRef = useRef<HTMLInputElement>(null);

  const categories = ['all', 'nutrition', 'mental-health', 'fitness', 'chronic-conditions', 'preventive-care'];

  // Mock experts for tagging
  const experts = [
    { id: 'expert1', name: 'Dr. Ama Osei', username: '@dr_ama', specialization: 'General Medicine' },
    { id: 'expert2', name: 'Dr. Kwaku Mensah', username: '@dr_kwaku', specialization: 'Mental Health' },
    { id: 'expert3', name: 'Dr. Akosua Frimpong', username: '@dr_akosua', specialization: 'Pediatrics' }
  ];

  useEffect(() => {
    if (navigationData) {
      if (navigationData.searchQuery) {
        setSearchTerm(navigationData.searchQuery);
        setSearchHistory(prev => {
          const newHistory = [navigationData.searchQuery, ...prev.filter(item => item !== navigationData.searchQuery)];
          return newHistory.slice(0, 5);
        });
      }
      
      if (navigationData.activeTab) {
        setActiveTab(navigationData.activeTab);
      }
      
      if (navigationData.openArticleView && navigationData.articleId) {
        const article = articles.find(a => a.id === navigationData.articleId);
        if (article) {
          setSelectedArticle(article);
        }
      }
      
      if (navigationData.type === 'drug') {
        setActiveTab('drugs');
        if (navigationData.searchQuery) {
          setSearchTerm(navigationData.searchQuery);
        }
      }

      if (navigationData.type === 'ask-expert') {
        setShowAskExpert(true);
      }
    }
  }, [navigationData, articles]);

  const filteredArticles = articles.filter(article => 
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredDrugs = drugs.filter(drug => 
    drug.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    drug.genericName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    drug.uses.some(use => use.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle auth-required actions
  const handleAskExpertClick = () => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to ask an expert', 'signup');
      return;
    }
    setShowAskExpert(true);
  };

  const handleArticleInteraction = (action: string) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to interact with articles', 'signup');
      return;
    }
    // Handle article interaction
  };

  // Handle backdrop clicks for modal dismissal
  const handleModalBackdropClick = (e: React.MouseEvent, closeModal: () => void) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const SearchBar = () => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    const suggestions = [
      'Drug interactions',
      'Pregnancy nutrition',
      'Blood pressure management',
      'Diabetes care',
      'Mental health support'
    ];

    // Handle search input change without losing focus
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    };

    const handleSuggestionClick = (suggestion: string) => {
      setSearchTerm(suggestion);
      setShowSuggestions(false);
      // Keep focus on input after suggestion selection
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
    };

    return (
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          ref={searchInputRef}
          type="text"
          placeholder={activeTab === 'articles' ? 'Search health articles...' : 'Search medications...'}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="input-base pl-10 dark:bg-gray-800 dark:border-gray-700"
        />
        
        {showSuggestions && (searchHistory.length > 0 || suggestions.length > 0) && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-10 max-h-64 overflow-y-auto">
            {searchHistory.length > 0 && (
              <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Searches</h4>
                {searchHistory.map((term, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(term)}
                    className="block w-full text-left p-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750 rounded"
                  >
                    🕒 {term}
                  </button>
                ))}
              </div>
            )}
            
            <div className="p-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Popular Searches</h4>
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="block w-full text-left p-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750 rounded"
                >
                  🔍 {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const AskExpertModal = () => {
    const [question, setQuestion] = useState('');
    const [category, setCategory] = useState('general');
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [taggedExpert, setTaggedExpert] = useState('');
    const [showExpertDropdown, setShowExpertDropdown] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (question.trim()) {
        onNavigate('community', {
          createPost: true,
          content: question,
          category,
          isAnonymous,
          postType: 'question',
          expertTag: taggedExpert
        });
        setShowAskExpert(false);
      }
    };

    const handleExpertTag = (expert: any) => {
      setTaggedExpert(expert.username);
      setQuestion(prev => prev + ` ${expert.username}`);
      setShowExpertDropdown(false);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => handleModalBackdropClick(e, () => setShowAskExpert(false))}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Ask an Expert</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-1">Get professional advice anonymously</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="select-base dark:bg-gray-800 dark:border-gray-700"
              >
                {categories.slice(1).map(cat => (
                  <option key={cat} value={cat}>
                    {cat.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Question</label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={4}
                placeholder="Ask your health question... Our verified experts will respond."
                className="textarea-base dark:bg-gray-800 dark:border-gray-700"
                required
              />
            </div>

            {/* Expert Tagging */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tag an Expert (Optional)</label>
              <button
                type="button"
                onClick={() => setShowExpertDropdown(!showExpertDropdown)}
                className="input-base text-left hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-750 transition-colors"
              >
                {taggedExpert ? taggedExpert : 'Select an expert to tag...'}
              </button>
              
              {showExpertDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-10">
                  {experts.map((expert) => (
                    <button
                      key={expert.id}
                      type="button"
                      onClick={() => handleExpertTag(expert)}
                      className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="avatar-sm">
                          {expert.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{expert.name}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{expert.username} • {expert.specialization}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="anonymous"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <label htmlFor="anonymous" className="text-sm text-gray-700 dark:text-gray-300">
                Ask anonymously (recommended)
              </label>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-blue-800 dark:text-blue-300 text-sm">
                <strong>Note:</strong> This is for educational purposes only. For emergencies, contact your local emergency services.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAskExpert(false)}
                className="btn-ghost dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Ask Community
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const ArticleModal = ({ article }: { article: HealthArticle }) => {
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [hasReacted, setHasReacted] = useState(false);

    const handleBookmark = () => {
      if (!currentUser && handleAuthRequired) {
        handleAuthRequired('Please sign in to bookmark articles', 'signup');
        return;
      }
      setIsBookmarked(!isBookmarked);
    };

    const handleReact = () => {
      if (!currentUser && handleAuthRequired) {
        handleAuthRequired('Please sign in to react to articles', 'signup');
        return;
      }
      setHasReacted(!hasReacted);
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => handleModalBackdropClick(e, () => setSelectedArticle(null))}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-custom">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{article.title}</h2>
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>By {article.author}</span>
                  <span className="flex items-center space-x-1">
                    <Clock size={14} />
                    <span>{article.readTime} min read</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Eye size={14} />
                    <span>{article.views} views</span>
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedArticle(null)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-2xl ml-4"
              >
                ×
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {article.coverImage && (
              <img 
                src={article.coverImage} 
                alt={article.title}
                className="w-full h-64 object-cover rounded-xl mb-6"
              />
            )}
            
            <div className="prose max-w-none dark:prose-invert">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">{article.excerpt}</p>
              <div className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-line">
                {article.content}
              </div>
            </div>
            
            {/* Action Bar */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleReact}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all ${
                      hasReacted 
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-750'
                    }`}
                  >
                    <Heart size={18} className={hasReacted ? 'fill-current' : ''} />
                    <span>{hasReacted ? article.likes + 1 : article.likes}</span>
                  </button>
                  
                  <button
                    onClick={handleBookmark}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all ${
                      isBookmarked 
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-750'
                    }`}
                  >
                    <Star size={18} className={isBookmarked ? 'fill-current' : ''} />
                    <span>{isBookmarked ? 'Saved' : 'Save'}</span>
                  </button>
                  
                  <button 
                    onClick={() => onNavigate('community', {
                      createPost: true,
                      content: `I just read an interesting article about "${article.title}" and wanted to share my thoughts...`,
                      category: article.category,
                      isAnonymous: false,
                      postType: 'text'
                    })}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-750 transition-colors"
                  >
                    <MessageCircle size={18} />
                    <span>Share</span>
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <span key={index} className="badge-secondary">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DrugModal = ({ drug }: { drug: Drug }) => (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => handleModalBackdropClick(e, () => setSelectedDrug(null))}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-custom">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{drug.name}</h2>
            <p className="text-gray-600 dark:text-gray-300">Generic: {drug.genericName}</p>
          </div>
          <button
            onClick={() => setSelectedDrug(null)}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Basic Information</h3>
              <div className="space-y-2">
                <p><span className="font-medium dark:text-gray-200">Category:</span> <span className="dark:text-gray-300">{drug.category}</span></p>
                <p><span className="font-medium dark:text-gray-200">Manufacturer:</span> <span className="dark:text-gray-300">{drug.manufacturer}</span></p>
                <p><span className="font-medium dark:text-gray-200">Prescription Required:</span> <span className="dark:text-gray-300">{drug.prescriptionRequired ? 'Yes' : 'No'}</span></p>
                <div className="flex items-center space-x-2">
                  <span className="font-medium dark:text-gray-200">Availability:</span>
                  <span className={`badge-base ${
                    drug.availability === 'in-stock' ? 'badge-success' :
                    drug.availability === 'limited' ? 'badge-warning' :
                    'badge-error'
                  }`}>
                    {drug.availability.replace('-', ' ')}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Description</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{drug.description}</p>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Uses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {drug.uses.map((use, index) => (
                <div key={index} className="flex items-center space-x-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-blue-800 dark:text-blue-300">{use}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Dosage</h3>
            <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">{drug.dosage}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Side Effects</h3>
              <div className="space-y-1">
                {drug.sideEffects.map((effect, index) => (
                  <div key={index} className="text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded">
                    {effect}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Contraindications</h3>
              <div className="space-y-1">
                {drug.contraindications.map((contra, index) => (
                  <div key={index} className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded">
                    {contra}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {drug.interactions.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Drug Interactions</h3>
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="space-y-1">
                  {drug.interactions.map((interaction, index) => (
                    <p key={index} className="text-yellow-800 dark:text-yellow-300 text-sm">{interaction}</p>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {drug.warnings.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Important Warnings</h3>
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="space-y-1">
                  {drug.warnings.map((warning, index) => (
                    <p key={index} className="text-red-800 dark:text-red-300 text-sm font-medium">{warning}</p>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                // Navigate to community with pre-filled post about this drug
                onNavigate('community', { 
                  createPost: true,
                  content: `I have a question about ${drug.name} (${drug.genericName}). ${drug.description}`,
                  category: 'general',
                  isAnonymous: false,
                  postType: 'question',
                  drugInfo: {
                    name: drug.name,
                    genericName: drug.genericName,
                    category: drug.category
                  }
                });
                setSelectedDrug(null);
              }}
              className="btn-secondary"
            >
              Ask Community
            </button>
            {drug.price && (
              <button
                onClick={() => onNavigate('shop', { productId: drug.id })}
                className="btn-primary"
              >
                Buy Now - ${drug.price}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20 lg:pb-0">
      {/* Sticky Top Bar with Search */}
      <div className="glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {currentUser ? 'Discover Health Knowledge' : 'Explore Health Cosmos'}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                {currentUser ? 'Explore our Health Cosmos and Drug Dictionary' : 'Preview our health articles and drug information'}
              </p>
              {navigationData?.filterResults && (
                <p className="text-purple-600 dark:text-purple-400 text-sm mt-1">
                  🔍 Filtered results for: "{navigationData.searchQuery}"
                </p>
              )}
            </div>
            
            {/* Ask Expert Anonymously CTA */}
            <button
              onClick={handleAskExpertClick}
              className="btn-primary flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600"
            >
              <HelpCircle size={20} />
              <span>Ask Expert Anonymously</span>
              {!currentUser && (
                <span className="badge-base bg-purple-700 text-white">Sign In</span>
              )}
            </button>
          </div>
          
          {/* Categories */}
          <div className="flex space-x-1 mb-6 glass rounded-2xl p-2 border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
            {[
              { id: 'articles', label: 'Health Articles', icon: Book },
              { id: 'drugs', label: 'Drug Dictionary', icon: Pill },
              { id: 'experts', label: 'Ask Expert', icon: User }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'experts') {
                      handleAskExpertClick();
                    } else {
                      setActiveTab(tab.id);
                    }
                  }}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          
          {/* Enhanced Search Bar */}
          <SearchBar />
        </div>
      </div>

      {/* Preview Notice for Non-Authenticated Users */}
      {!currentUser && (
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                <Eye size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 dark:text-blue-100">Health Cosmos Preview</h3>
                <p className="text-blue-700 dark:text-blue-300 text-sm">You're viewing a preview of our health knowledge base. Sign in to access full features like bookmarking, reactions, and expert consultations!</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'articles' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <div
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                className="card-interactive p-6 dark:bg-gray-800 dark:border-gray-700"
              >
                {article.coverImage && (
                  <img 
                    src={article.coverImage} 
                    alt={article.title}
                    className="w-full h-48 object-cover rounded-xl mb-4"
                  />
                )}
                
                <div className="flex items-center justify-between mb-3">
                  <span className="badge-primary">
                    {article.category}
                  </span>
                  {article.featured && (
                    <Star className="text-yellow-500 fill-current" size={16} />
                  )}
                </div>
                
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-purple-700 dark:group-hover:text-purple-400 transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
                
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center space-x-1">
                      <Clock size={12} />
                      <span>{article.readTime} min</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Eye size={12} />
                      <span>{article.views}</span>
                    </span>
                  </div>
                  <ChevronRight className="text-purple-500 dark:text-purple-400 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDrugs.map((drug) => (
              <div
                key={drug.id}
                onClick={() => setSelectedDrug(drug)}
                className="card-interactive p-6 dark:bg-gray-800 dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center">
                    <Pill className="text-white" size={24} />
                  </div>
                  <div className="flex items-center space-x-2">
                    {drug.prescriptionRequired && (
                      <span className="badge-error">
                        Rx
                      </span>
                    )}
                    <span className={`badge-base ${
                      drug.availability === 'in-stock' ? 'badge-success' :
                      drug.availability === 'limited' ? 'badge-warning' :
                      'badge-error'
                    }`}>
                      {drug.availability.replace('-', ' ')}
                    </span>
                  </div>
                </div>
                
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
                  {drug.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{drug.genericName}</p>
                <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-2">{drug.description}</p>
                
                <div className="flex items-center justify-between">
                  <span className="badge-secondary">
                    {drug.category}
                  </span>
                  <ChevronRight className="text-blue-500 dark:text-blue-400 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </div>
            ))}
          </div>
        )}
        
        {((activeTab === 'articles' && filteredArticles.length === 0) || 
          (activeTab === 'drugs' && filteredDrugs.length === 0)) && (
          <div className="text-center py-12">
            {activeTab === 'articles' ? (
              <Book className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
            ) : (
              <Pill className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
            )}
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No results found</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">Try adjusting your search terms or browse all content.</p>
            <button
              onClick={handleAskExpertClick}
              className="btn-primary"
            >
              Ask an Expert Instead
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedArticle && <ArticleModal article={selectedArticle} />}
      {selectedDrug && <DrugModal drug={selectedDrug} />}
      {showAskExpert && <AskExpertModal />}
    </div>
  );
};

export default Discover;